import React from "react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useReactiveVar } from "@apollo/client";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import {
    useCreateMenuCategoryMutation,
    useGetMenuCategoriesIconsQuery,
    useRestoreMenuCategoryMutation,
    useUpdateMenuCategoryMutation,
} from "@me-team/host/main/menu/graphql/menu.hooks";
import { Formik, Form as FormikForm, Field } from "formik";
import { CategoriesDishesValidationSchema } from "./CategoriesDishesValidationSchema";
import TextInput from "@services/ui-components/src/TextInput/TextInput";
import { Spinner, SpinnerLoader } from "@services/ui-components";
import { CompanyAccess, MenuCategoryInput, MenuCategoryProxy } from "@me-team/host/main/graphql/types";
import { CategoryFormModalMode } from "./interfaces";
import { LanguageVm } from "../../../interfaces/meMenuInterfaces/interfaces";
import { buildTranslationTextVm } from "../utils/translationVersions/buildTranslationTextVm";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";
import { useParams } from "react-router-dom";
import CategoryIconPicker from "../components/CategoryIconPicker/CategoryIconPicker";
import { getCopyOriginProps } from "@me-team/common/src/components/Me-menu/utils/translationVersions/getCopyOriginProps";
import CopyOriginButton from "@me-team/common/src/components/Me-menu/components/CopyOriginButton/CopyOriginButton";
import { TranslationLockedFieldsInfoBanner } from "@me-team/common/src/components/Me-menu/components/TranslationLockedFieldsInfoBanner/TranslationLockedFieldsInfoBanner";

interface CategoryFormModalProps {
    show: boolean;
    handleClose: () => void;
    category: MenuCategoryProxy | null;
    parentCategory: MenuCategoryProxy | null;
    mode: CategoryFormModalMode;
    onSave: () => void;
    toggleShowToast: (text: string) => void;
    primaryLanguageVm: LanguageVm;
    selectedLanguageVm: LanguageVm;
    canCreateCategory?: boolean;
    canEditCategory?: boolean;
    canDeleteRestoreCategory?: boolean;
}

const CategoryFormModal: React.FC<CategoryFormModalProps> = ({
    show,
    handleClose,
    category,
    parentCategory,
    mode,
    onSave,
    toggleShowToast,
    primaryLanguageVm,
    selectedLanguageVm,
    canCreateCategory,
    canEditCategory,
    canDeleteRestoreCategory,
}) => {
    const { t } = useTranslation();
    const { branchId } = useParams();
    const basePath = process.env.REACT_APP_POS_URL;
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const isCreateMode = mode === "create";
    const isEditMode = mode === "edit";
    const isRestoreMode = mode === "restore";
    const isTranslationMode = !isCreateMode && primaryLanguageVm.code !== selectedLanguageVm.code;
    const selectedLanguage: LanguageVm = isCreateMode ? primaryLanguageVm : selectedLanguageVm;
    const canCreateCategoryByRole = useRoleAccess(CompanyAccess.MenuDishCategoryCreate);
    const canEditCategoryByRole = useRoleAccess(CompanyAccess.MenuDishCategoryEdit);
    const canDeleteRestoreCategoryByRole = useRoleAccess(CompanyAccess.MenuDishCategoryDeleteRestore);
    const canSubmitCreate = canCreateCategory ?? canCreateCategoryByRole;
    const canSubmitEdit = canEditCategory ?? canEditCategoryByRole;
    const canSubmitRestore = canDeleteRestoreCategory ?? canDeleteRestoreCategoryByRole;
    const canSubmit = isCreateMode ? canSubmitCreate : isEditMode ? canSubmitEdit : canSubmitRestore;

    const { data: iconsData, loading: iconsDataLoading } = useGetMenuCategoriesIconsQuery({
        variables: { companyId: Number(currentCompanyId) },
        skip: !currentCompanyId,
        context: { errorType: "global" },
    });

    const categoryIcons = iconsData?.user?.company?.menuCategoryIconList || [];

    const [createMenuCategory] = useCreateMenuCategoryMutation();
    const [updateMenuCategory] = useUpdateMenuCategoryMutation();
    const [restoreMenuCategory] = useRestoreMenuCategoryMutation();

    const determineMessages = () => {
        if (isCreateMode) {
            return parentCategory
                ? {
                      title: t("Create New Category in", { parentName: parentCategory.name }),
                      buttonText: t("Create"),
                      submitButtonText: t("Creating..."),
                  }
                : { title: t("Create New Category"), buttonText: t("Create"), submitButtonText: t("Creating...") };
        }

        if (isEditMode) {
            return { title: t("Edit category"), buttonText: t("Save"), submitButtonText: t("Saving...") };
        }

        if (isRestoreMode) {
            return parentCategory
                ? {
                      title: t("Restore category in", { parentName: parentCategory.name }),
                      buttonText: t("Restore"),
                      submitButtonText: t("Restoring..."),
                  }
                : { title: t("Restore category"), buttonText: t("Restore"), submitButtonText: t("Restoring...") };
        }
    };

    const message = determineMessages();

    const initialValues = {
        name: category?.name || "",
        comment: category?.comment || "",
        faviconId: category?.favicon?.id || null,
        parentCategoryId: parentCategory?.id || null,
    };

    if (iconsDataLoading) return <Spinner />;

    return (
        <Modal centered show={show} onHide={handleClose} size="lg">
            <Modal.Header closeButton className="border-bottom-0 gap-3">
                <div className="d-flex flex-wrap gap-2 gap-lg-3">
                    <Modal.Title className="fs-5">{message.title}</Modal.Title>
                    <div className="d-flex justify-content-center align-items-center bg-violet-light rounded-1 p-1 px-6 ms-3">
                        <span className="fs-7 text-primary">{selectedLanguage.label}</span>
                    </div>
                </div>
            </Modal.Header>

            <Formik
                enableReinitialize
                initialValues={initialValues}
                validationSchema={CategoriesDishesValidationSchema(t)}
                onSubmit={async (values, { setFieldError }) => {
                    if (!canSubmit) return;
                    if (!currentCompanyId) return;
                    try {
                        const input: MenuCategoryInput = {
                            name: values.name.trim(),
                            comment: values.comment.trim() || null,
                            parentCategoryId: values.parentCategoryId || null,
                            faviconId: values.faviconId || null,
                        };

                        if (isCreateMode) {
                            await createMenuCategory({
                                variables: {
                                    companyId: Number(currentCompanyId),
                                    branchId: Number(branchId),
                                    input,
                                    locale: primaryLanguageVm.code,
                                },
                                onCompleted: () => {
                                    toggleShowToast(t("Created"));
                                },
                            });
                        }

                        if (category && isEditMode) {
                            await updateMenuCategory({
                                variables: {
                                    id: category.id,
                                    branchId: Number(branchId),
                                    companyId: Number(currentCompanyId),
                                    input,
                                    locale: selectedLanguage.code,
                                },
                                onCompleted: () => {
                                    toggleShowToast(t("Saved"));
                                },
                            });
                        }

                        if (category && isRestoreMode) {
                            await restoreMenuCategory({
                                variables: {
                                    id: category.id,
                                    branchId: Number(branchId),
                                },
                                onCompleted: () => {
                                    toggleShowToast(t("Restored"));
                                },
                            });
                        }
                        onSave();
                    } catch (err: any) {
                        const msg = err?.graphQLErrors?.[0]?.message || "";

                        if (msg.includes("already exists")) {
                            setFieldError("name", t("A category with this name already exists"));
                        } else {
                            setFieldError("name", t("An error occurred while creating the category"));
                        }
                    }
                }}
            >
                {({ values, isSubmitting, setFieldValue }) => {
                    const nameVm = buildTranslationTextVm({
                        translatedValue: values.name,
                        originValue: category?.nameOrigin,
                        isTranslationMode,
                    });
                    const commentVm = buildTranslationTextVm({
                        translatedValue: values.comment,
                        originValue: category?.descriptionOrigin,
                        isTranslationMode,
                    });
                    const commentPlaceholder =
                        commentVm.placeholderText ?? t("Enter a short category description (optional)");

                    return (
                        <FormikForm>
                            <Modal.Body className="border-bottom-0">
                                <div className="px-6 mb-3">
                                    {isTranslationMode && <TranslationLockedFieldsInfoBanner />}
                                    <Form.Group controlId="name" className="mb-3 mb-sm-4">
                                        <Row>
                                            <Col sm={3}>
                                                <label
                                                    htmlFor="categoryModal-name-field"
                                                    className="fs-7 fw-bold text-start mb-sm-0 required-label pt-2"
                                                >
                                                    {t("Category Name")}
                                                </label>
                                            </Col>
                                            <TextInput
                                                id="categoryModal-name-field"
                                                inputCol={9}
                                                name="name"
                                                label=""
                                                placeholder={nameVm.placeholderText ?? "Enter a category name"}
                                                isLabel={false}
                                                maxLength={50}
                                                infoText={`${values.name.length}/50`}
                                                rightSlot={
                                                    nameVm.isMissingTranslation ? (
                                                        <CopyOriginButton
                                                            {...getCopyOriginProps(nameVm)}
                                                            onCopied={() => toggleShowToast(t("Original text copied"))}
                                                        />
                                                    ) : null
                                                }
                                            />
                                        </Row>
                                    </Form.Group>

                                    <Form.Group controlId="comment" className="mb-3 mb-sm-4">
                                        <Row>
                                            <Col sm={3}>
                                                <label
                                                    htmlFor="categoryModal-description-field"
                                                    className="fs-7 fw-bold text-start mb-sm-0 pt-2"
                                                >
                                                    {t("Description")}
                                                </label>
                                            </Col>
                                            <Col sm={9}>
                                                <div className="position-relative">
                                                    <Field
                                                        id="categoryModal-description-field"
                                                        name="comment"
                                                        as="textarea"
                                                        className="form-control pe-5 rounded-1"
                                                        maxLength={400}
                                                        value={values.comment}
                                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                            setFieldValue("comment", e.target.value)
                                                        }
                                                        placeholder={commentPlaceholder}
                                                    />
                                                    <div className="position-absolute top-0 end-0 mt-1 me-2">
                                                        <CopyOriginButton
                                                            {...getCopyOriginProps(commentVm)}
                                                            onCopied={() => toggleShowToast(t("Original text copied"))}
                                                        />
                                                    </div>
                                                </div>
                                                <Form.Text className="text-grey fs-7 ps-2">
                                                    {values.comment.length}/400
                                                </Form.Text>
                                            </Col>
                                        </Row>
                                    </Form.Group>

                                    <Form.Group controlId="faviconId">
                                        <Row>
                                            <Col sm={3}>
                                                <label
                                                    htmlFor="categoryModal-icon-field"
                                                    className="fs-7 fw-bold text-start pt-sm-2 mb-sm-0"
                                                >
                                                    {t("Category Icon")}
                                                </label>
                                            </Col>
                                            <Col sm={9}>
                                                <CategoryIconPicker
                                                    icons={categoryIcons}
                                                    value={values.faviconId}
                                                    onChange={(iconId) => setFieldValue("faviconId", iconId)}
                                                    disabled={isTranslationMode}
                                                    basePath={basePath}
                                                />
                                            </Col>
                                        </Row>
                                    </Form.Group>
                                </div>
                            </Modal.Body>

                            <Modal.Footer className="justify-content-between border-top-0 flex-nowrap">
                                <Button
                                    variant="outline-primary"
                                    className="custom-btn"
                                    onClick={handleClose}
                                    disabled={isSubmitting}
                                >
                                    {t("cancel")}
                                </Button>

                                <Button
                                    variant="primary"
                                    className="custom-btn fw-bold"
                                    type="submit"
                                    disabled={isSubmitting || !canSubmit}
                                >
                                    {isSubmitting ? (
                                        <SpinnerLoader variant="light" loadingText={message.submitButtonText} />
                                    ) : (
                                        message.buttonText
                                    )}
                                </Button>
                            </Modal.Footer>
                        </FormikForm>
                    );
                }}
            </Formik>
        </Modal>
    );
};

export default CategoryFormModal;
